import BaseSingleton from './base_singleton';
import _ from '../utils/underscore';
import ServiceFactory from '../services/service_factory';
import Event, {SystemEventSubscription} from '../utils/event';
import { SYSTEM_EVENTS } from '../events/events';
import IBaseService, { CreateServiceFactoryFunction, ServiceDependencies } from './base_service_interface';


export default class BaseService extends BaseSingleton implements IBaseService {
    protected featureEnabled: boolean = true;
    private _isToggledAlready = false;
    private _systemEventsSubscriptions = new Array<SystemEventSubscription>();

    protected init(params: any): void {
        super.init(params);
        if (params && params.featureEnabled === false) {
            this.featureEnabled = false;
        }

        if(this.shouldApplyFeatureToggleABTesting()) {
            Event.on([SYSTEM_EVENTS.OAUTH_STATE_CHANGE, SYSTEM_EVENTS.ON_INIT_COMPLETE], ()=>this.applyABTestingLogic(params), this)
        } else {
            Event.on(SYSTEM_EVENTS.ON_INIT_COMPLETE, ()=>this.toggleFeatureOn(params), this)
        }
    }

    private applyABTestingLogic(params: any): void{
        if(!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return;
        }
        const abTestingParams = this.serviceFactory.abTestingService().getAbTestingDataForFeature(this.getClassName());
        if (abTestingParams && abTestingParams.Properties){
            const featureToggleProperty = this._safeGetPropertyLowerCase(abTestingParams.Properties,'featuretoggle');
            if (featureToggleProperty === 'true' && !this._isToggledAlready) {
               this._isToggledAlready = true;
               this.toggleFeatureOn(params);
            }
        }
    }

    protected shouldApplyFeatureToggleABTesting(): boolean {
        //override this function if service supports AB testing
        return false;
    }

    protected toggleFeatureOn(params: any): void{
        //override this function if service need to be initialized after getting AB testing parameters
    }

    private _safeGetPropertyLowerCase(properties,propertyName): string{
        if (properties[propertyName]){
            return properties[propertyName].toLowerCase();
        } else {
            return '';
        }
    }

    protected featureToggle(){
        return this.featureEnabled;
    }
    /**
     * can be override when service has async jobs to execute before app init complete.
     */
    doAsyncJobs(): Promise<any> {
        return Promise.resolve();
    }


    /**
     * can be override when service has async jobs to execute after all services have being initialized
     */
    onInitializationCompleted(): Promise<any> {
        return Promise.resolve();
    }

    private _createServiceFactoryFunction: CreateServiceFactoryFunction = null;
    protected get serviceFactory(): ServiceFactory {
        if (_.isNullOrUndefined(this._createServiceFactoryFunction)) {
            this.throwError('79', 'serviceFactory is not available at this time because it is not injected yet!', {className: this.getClassName()});
        }
        return this._createServiceFactoryFunction();
    }

    injectDependencies(dependencies: ServiceDependencies): void {
        this._createServiceFactoryFunction = dependencies.createServiceFactoryFunction;
    }

    // only for unit tests - so we will stop listen to events
    shutDown() {
        this._detachSystemEvents();
    }

    protected onSystemEvent(eventName: string | string[], callback: Function): SystemEventSubscription {
        const subscription = Event.on(eventName, callback.bind(this), this);
        this._systemEventsSubscriptions.push(subscription);
        return this._createEventSubscription(subscription);
    }

    protected onSystemEventOnce(eventName: string | string[], callback: Function): SystemEventSubscription {
        const subscription = Event.once(eventName, callback.bind(this), this);
        this._systemEventsSubscriptions.push(subscription);
        return this._createEventSubscription(subscription);
    }

    private _detachSystemEvents(): void {
        Event.detachEvents(this._systemEventsSubscriptions);
        this._systemEventsSubscriptions = new Array<SystemEventSubscription>();
    }

    private _createEventSubscription(subscription: SystemEventSubscription): SystemEventSubscription {
        return {
            off: () => {
                subscription.off();
                const index = this._systemEventsSubscriptions.findIndex(item => item === subscription);
                if (index >= 0) {
                    this._systemEventsSubscriptions.splice(index, 1);
                }
            }
        }
    }
}

